import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import Loader from "../../../components/Loader";
import { formatDate as fmtDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import { getUser } from "../../../config/auth";
import { canReviewTimeline } from "../../../config/rbac";

const STATUS_STYLE = {
  Pending:  { bg: "#fef3c7", color: "var(--warn-text)" },
  Approved: { bg: "#d1fae5", color: "#065f46" },
  Rejected: { bg: "#fee2e2", color: "var(--danger-text)" },
};

export default function TimelineRequestsPage() {
  const user = getUser();
  const role = user?.role || "Staff";
  const allowed = canReviewTimeline(role);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviewing, setReviewing] = useState(null); // requestId being actioned
  const [reviewModal, setReviewModal] = useState(null); // { req, action }
  const [reviewComment, setReviewComment] = useState("");
  const [reviewError, setReviewError] = useState("");

  useEffect(() => { if (allowed) load(); }, [allowed]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/timeline-changes/pending");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  const openReviewModal = (req, action) => {
    setReviewComment("");
    setReviewError("");
    setReviewModal({ req, action });
  };

  async function submitReview() {
    if (!reviewModal) return;
    const { req, action } = reviewModal;
    setReviewError("");
    setReviewing(req.id);
    try {
      const res = await apiFetch(
        `/api/tasks/${req.taskId}/timeline-changes/${req.id}/${action}`,
        { method: "PUT", body: JSON.stringify({ comment: reviewComment || "" }) }
      );
      if (!res.ok) throw new Error(await res.text());
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
      setReviewModal(null);
      setReviewComment("");
    } catch (e) {
      setReviewError(e?.message || "Failed to submit review");
    } finally {
      setReviewing(null);
    }
  }

  if (!allowed) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <div style={{ fontWeight: 600, fontSize: 15 }}>You don't have permission to view this page.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ marginBottom: 4 }}>Timeline Change Requests</h2>
        <p style={{ color: "var(--text-2)", fontSize: 14 }}>
          Pending due-date extension requests across all tasks
        </p>
      </div>

      {error && (
        <div style={{
          padding: "12px 14px", borderRadius: 10, marginBottom: 16,
          border: "1px solid var(--danger-border)", background: "var(--danger-soft)",
          color: "var(--danger-text)", fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <div className="glass-card" style={{ padding: 0 }}>
        <div className="tasks-table">
          {/* Header */}
          <div
            className="tasks-row header"
            style={{ gridTemplateColumns: "2fr 1.2fr 1.1fr 1.1fr 2fr 1.4fr" }}
          >
            <div>Task</div>
            <div>Requested By</div>
            <div>Current Due</div>
            <div>Requested Date</div>
            <div>Reason</div>
            <div>Actions</div>
          </div>

          {loading && <Loader.TableRows rows={5} />}

          {!loading && requests.length === 0 && (
            <EmptyState
              icon="📅"
              title="No timeline change requests"
              subtitle="Requests to extend task deadlines will appear here."
            />
          )}

          {!loading && requests.map((r) => {
            const ss = STATUS_STYLE[r.status] || STATUS_STYLE.Pending;
            const busy = reviewing === r.id;
            return (
              <div
                key={r.id}
                className="tasks-row"
                style={{ gridTemplateColumns: "2fr 1.2fr 1.1fr 1.1fr 2fr 1.4fr" }}
              >
                {/* Task */}
                <div>
                  <Link
                    to={`/tasks/${r.taskId}`}
                    className="task-title"
                    style={{ textDecoration: "none" }}
                  >
                    {r.taskTitle || "Unnamed Task"}
                  </Link>
                </div>

                {/* Requested by */}
                <div style={{ fontSize: 13, color: "var(--text-2)" }}>
                  {r.requestedByName || "—"}
                </div>

                {/* Original due date */}
                <div style={{ fontSize: 13, color: "var(--text-2)" }}>
                  {fmtDate(r.originalDueDate)}
                </div>

                {/* Requested date */}
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--brand)" }}>
                  {fmtDate(r.requestedDueDate)}
                </div>

                {/* Reason */}
                <div
                  title={r.reason}
                  style={{
                    fontSize: 12, color: "var(--text-2)", lineHeight: 1.4,
                    display: "-webkit-box", WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical", overflow: "hidden",
                    cursor: "default",
                  }}
                >
                  {r.reason}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{
                    padding: "2px 9px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: ss.bg, color: ss.color,
                  }}>
                    {r.status}
                  </span>
                  {r.status === "Pending" && (
                    <>
                      <button
                        className="btn"
                        style={{ padding: "4px 10px", borderRadius: 8, fontSize: 12 }}
                        disabled={busy}
                        onClick={() => openReviewModal(r, "approve")}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-ghost"
                        style={{ padding: "4px 10px", borderRadius: 8, fontSize: 12 }}
                        disabled={busy}
                        onClick={() => openReviewModal(r, "reject")}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review Comment Modal */}
      {reviewModal && (
        <div className="modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="glass-card modal" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440, width: "95vw" }}>
            <div className="modal-header">
              <h3>
                {reviewModal.action === "approve" ? "✅ Approve Request" : "❌ Reject Request"}
              </h3>
              <button className="modal-close" onClick={() => setReviewModal(null)}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
              Task: <strong>{reviewModal.req.taskTitle}</strong>
            </div>
            {reviewError && (
              <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {reviewError}
              </div>
            )}
            <label style={{ fontSize: 13, color: "var(--text-2)", display: "block", marginBottom: 6 }}>
              Comment {reviewModal.action === "reject" ? "*" : "(optional)"}
            </label>
            <textarea
              autoFocus
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder={reviewModal.action === "approve" ? "Add a note (optional)..." : "Reason for rejection..."}
              rows={3}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border-strong)", fontFamily: "inherit", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
            />
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setReviewModal(null)}>Cancel</button>
              <button
                onClick={submitReview}
                disabled={reviewing !== null || (reviewModal.action === "reject" && !reviewComment.trim())}
                style={{
                  padding: "8px 20px", borderRadius: 10, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer",
                  background: reviewModal.action === "approve" ? "#16a34a" : "#dc2626",
                  color: "white", opacity: (reviewing !== null || (reviewModal.action === "reject" && !reviewComment.trim())) ? 0.5 : 1,
                }}
              >
                {reviewing !== null
                  ? reviewModal.action === "approve" ? "Approving..." : "Rejecting..."
                  : reviewModal.action === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
